import clsx from "clsx";
import * as Popper from "popper.js";
import React, { FC, memo, useEffect, useState } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import InputMask from "react-input-mask";
import { ru } from "date-fns/locale";
import type { Control, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form/dist/index.ie11";

import Cross from "~/components/Icons/icon-cross";
import { getLastDayOfMonth } from "~/libs/getFormatDate";

import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-overrides.less";
import s from "./styles.less";

registerLocale("ru", ru);
setDefaultLocale("ru");

interface Props extends Omit<
  IDatePickerRawProps,
  "idx" | "value" | "onChange"
> {
  name: string;
  control: Control<FieldValues>;
  defaultValue?;
}

const DatesPicker: FC<Props> = ({
  name,
  control,
  defaultValue = null,
  ...datePickerProps
}) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ value, onChange }) => (
        <DatePickerRaw
          idx={name}
          value={value}
          onChange={onChange}
          {...datePickerProps}
        />
      )}
    />
  );
};

interface IDatePickerRawProps {
  idx: string;
  id?: string;
  title?: string;
  dealerTitle?: string;
  label?: string;
  error?: string;
  errorBythDay?: boolean;
  errosStyle?: boolean;
  dateFormat?: string;
  showTimeSelect?: boolean;
  disabled?: boolean;
  showMonthYear?: boolean;
  showFullMonthYearPicker?: boolean;
  gray?: boolean;
  onSelect?: (e) => void;
  onKeyDown?: (e) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  customClassName?: string;
  dealerTitleClassName?: string;
  customClassError?: string;
  customLabel?: string;
  startDate?;
  endDate?;
  popperPlacement?: Popper.Placement;
  placeholder?: string;
  showYearDropdown?;
  showMonthDropdown?;
  clearDisabled?: boolean;
  filterDate?: (date: Date) => boolean;
  showYearPicker?: boolean;
  customInput?: React.ReactNode;
  value: Date | null | undefined;
  onChange: (value: Date | null | undefined) => void;
}

export const DatePickerRaw = (props: IDatePickerRawProps) => {
  const {
    idx,
    id = "",
    title,
    dealerTitle,
    label,
    error,
    errorBythDay,
    errosStyle,
    dateFormat = "dd.MM.yyyy, hh:mm",
    showTimeSelect,
    showMonthYear = false,
    showFullMonthYearPicker = false,
    disabled = false,
    onSelect,
    minDate,
    maxDate,
    gray = false,
    customClassName = "",
    customClassError = "",
    customLabel = "",
    startDate,
    endDate,
    popperPlacement = "bottom",
    placeholder = "",
    showYearDropdown = true,
    showMonthDropdown = true,
    onKeyDown,
    clearDisabled,
    filterDate,
    showYearPicker = false,
    customInput,
    value,
    onChange,
    dealerTitleClassName,
  } = props;

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isFocused) {
      const excludedMonths = document.querySelectorAll(
        'div[aria-label~="Not available"]',
      );
      console.log(
        document.getElementsByClassName("react-datepicker__month_text"),
      );
      console.log(excludedMonths, "excludedMonths");
    }
  }, [isFocused]);

  const clearVal = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (value !== undefined && value !== null) {
      onChange(undefined);
    }
  };

  const currentMask: string = showYearPicker
    ? "9999"
    : showMonthYear
      ? "99.9999"
      : "99.99.9999";

  return (
    <>
      {title && <div className={s.dp_title}>{title}</div>}
      {dealerTitle && <div className={clsx(s.dp_dealerTitle, dealerTitleClassName)}>{dealerTitle}</div>}
      <div
        className={clsx(s.container_date_picker, {
          [s.with_title]: title || gray,
          [s.with_dealerTitle]: dealerTitle,
          [s.error_input]: error,
          [customClassName]: true,
        })}
      >
        {label && (
          <label
            htmlFor={idx}
            className={clsx({
              [s.active]:
                (isFocused && !customLabel) || (value && !customLabel),
            })}
          >
            <div
              className={clsx({
                [s.label]: true,
                [s.customLabel]: true,
                [s.label_active]:
                  (isFocused && !customLabel) || (value && !customLabel),
              })}
            >
              {label}
            </div>
          </label>
        )}

        <DatePicker
          customInput={
            // maskedPicker &&
            customInput ?? (
              <InputMask
                type={"nunber"}
                mask={currentMask}
                beforeMaskedValueChange={(rawValue) => {
                  const maskForCompare = currentMask.replaceAll("9", "_");
                  if (
                    rawValue.value === maskForCompare &&
                    value !== undefined &&
                    value !== null
                  ) {
                    clearVal();
                  }
                  return rawValue;
                }}
                name={idx}
                id={id}
                disabled={disabled}
                onChange={onChange}
                placeholder={placeholder}
                value={value}
              />
            )
          }
          filterDate={filterDate}
          id={id || idx}
          selected={value}
          showTimeSelect={showTimeSelect}
          locale="ru"
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          onSelect={onSelect}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          showYearDropdown={showYearDropdown}
          showMonthDropdown={showMonthDropdown}
          onChange={(val: Date) => {
            const changedValue = showMonthYear
              ? getLastDayOfMonth(new Date(val))
              : val;
            return onChange(changedValue);
          }}
          dateFormat={dateFormat}
          startDate={startDate}
          endDate={endDate}
          autoComplete={"off"}
          monthClassName={() => {
            if (!!filterDate) {
              return s.filteredResolveClass;
            }
          }}
          showYearPicker={showYearPicker}
          showMonthYearPicker={showMonthYear}
          showFullMonthYearPicker={showFullMonthYearPicker}
          popperPlacement={popperPlacement}
          placeholderText={placeholder}
          onKeyDown={onKeyDown}
          className={clsx({
            [s.disabled]: disabled,
          })}
        />
        {!showYearPicker && !disabled && !onKeyDown && !clearDisabled && (
          <div
            className={s.clearBtn}
            onClick={(e) => {
              clearVal(e);
            }}
          >
            <Cross />
          </div>
        )}
        {error && (
          <div
            className={clsx(s.error, {
              [s.errosStyle]: errosStyle,
              [customClassError]: true,
            })}
          >
            {" "}
            {error}{" "}
          </div>
        )}
        {errorBythDay && (
          <div className={s.error}> Человеку обязано быть больше 18 лет </div>
        )}
      </div>
    </>
  );
};

export default memo(DatesPicker);
